import { registerHomepagePreload, unregisterHomepagePreload } from '@theme/resource-preload';
import { HomepageVideoPolicy } from '@theme/homepage-video-policy';
import { Component } from '@theme/component';
import { watchVideoFirstFrame } from '@theme/video-first-frame';

/**
 * A video that auto-plays when it scrolls into view.
 *
 * @typedef {object} Refs
 * @property {HTMLVideoElement} video - The video element.
 * @property {HTMLButtonElement} [playPauseButton] - The play/pause toggle button.
 * @property {HTMLButtonElement} [playOverlay] - The center play button shown when autoplay is disabled.
 * @property {HTMLButtonElement} [soundButton] - The sound toggle button.
 *
 * @extends {Component<Refs>}
 */
export class InViewVideo extends Component {
  requiredRefs = ['video'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HomepageVideoPolicy} */
  #policy;
  #visible = false;

  #stopWatchingFrame;

  /** Restart from the beginning, preserving visibility-based playback. */
  restart({ userInitiated = false } = {}) {
    this.#pause();
    // Keep the user's selection authorized across visibility pauses, but reset
    // it on the next restart (including selections from the theme editor).
    this.#policy.respectNetwork = !userInitiated;
    if (userInitiated) this.#policy.blocked = false;
    this.refs.video.currentTime = 0;
    // Re-evaluate visibility even when several tab switches occur in one frame.
    if (this.#observer) {
      this.#observer.unobserve(this);
      this.#observer.observe(this);
    }
  }

  /** Buffer without starting playback or changing the poster. */
  preload() {
    return this.#policy?.preload() ?? false;
  }

  /** Let a carousel suspend hidden slides and resume the selected video in view. */
  setAutoplay(enabled) {
    this.toggleAttribute('autoplay', enabled);
    if (!this.#policy) return;
    if (!enabled) this.#pause();
    else if (this.#visible && !document.hidden
      && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.#play();
  }

  connectedCallback() {
    super.connectedCallback();

    const { video } = this.refs;

    this.#policy = new HomepageVideoPolicy(video, {
      enabled: this.hasAttribute('data-homepage-video'),
      firstFrameTimeout: Number(this.getAttribute('data-first-frame-timeout') ?? 3000),
      fallback: () => {
        this.classList.remove('is-video-ready');
        if (this.refs.playOverlay) this.refs.playOverlay.hidden = false;
        this.#updatePlayPauseState();
      },
    });
    registerHomepagePreload(this);
    this.#stopWatchingFrame = watchVideoFirstFrame(video, {
      ready: () => { this.#policy.presented(); this.classList.add('is-video-ready'); },
      reset: () => this.classList.remove('is-video-ready'),
    });

    if (this.refs.playPauseButton) {
      this.refs.playPauseButton.addEventListener('click', this.#handlePlayPauseClick);
    }

    if (this.refs.playOverlay) {
      this.refs.playOverlay.addEventListener('click', this.#handleOverlayClick);
    }

    if (this.refs.soundButton) {
      this.refs.soundButton.classList.add('is-muted');
      this.refs.soundButton.addEventListener('click', this.#handleSoundClick);
    }

    if (this.hasAttribute('data-homepage-video')) document.addEventListener('visibilitychange', this.#onVisibilityChange);
    video.addEventListener('play', this.#updatePlayPauseState);
    video.addEventListener('pause', this.#onPause);
    this.#updatePlayPauseState();

    if (
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#visible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
        if (this.#visible && (!this.hasAttribute('data-homepage-video') || !document.hidden)) {
          if (this.hasAttribute('autoplay')) this.#play();
        } else {
          this.#pause();
        }
      },
      { threshold: 0.2 }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    unregisterHomepagePreload(this);
    super.disconnectedCallback();
    const { video } = this.refs;
    video.removeEventListener('play', this.#updatePlayPauseState);
    video.removeEventListener('pause', this.#onPause);
    this.refs.playPauseButton?.removeEventListener('click', this.#handlePlayPauseClick);
    this.refs.playOverlay?.removeEventListener('click', this.#handleOverlayClick);
    this.refs.soundButton?.removeEventListener('click', this.#handleSoundClick);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#stopWatchingFrame?.();
    this.#policy?.destroy();
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
  }

  #onPause = () => {
    // A queued pause from the previous selection can arrive after play() has
    // resumed this video. Do not release the new selection's pending load.
    if (!this.refs.video.paused) return;
    this.#policy?.pause();
    this.#updatePlayPauseState();
  };

  #onVisibilityChange = () => {
    if (document.hidden) this.#pause();
    else if (this.#visible && this.hasAttribute('autoplay')) this.#play();
  };

  #handlePlayPauseClick = () => {
    const { video } = this.refs;
    if (video.paused) this.#play(false);
    else video.pause();
  };

  #handleOverlayClick = () => {
    this.#play(false);
  };

  #handleSoundClick = () => {
    const { video, soundButton } = this.refs;
    if (!(soundButton instanceof HTMLElement)) return;

    video.muted = !video.muted;
    soundButton.classList.toggle('is-muted', video.muted);
    soundButton.setAttribute('aria-label', video.muted ? 'Enable sound' : 'Mute');
  };

  #updatePlayPauseState = () => {
    const { video, playPauseButton, playOverlay } = this.refs;
    const isPlaying = !video.paused;

    if (playPauseButton instanceof HTMLElement) {
      playPauseButton.classList.toggle('is-playing', isPlaying);
      playPauseButton.setAttribute('aria-label', isPlaying ? 'Pause' : 'Play');
    }

    if (playOverlay instanceof HTMLElement) {
      playOverlay.classList.toggle('is-hidden', isPlaying);
    }
  };

  #play(automatic = true) {
    if (!this.isConnected || this.closest('[hidden]')) return;
    if (!automatic && this.hasAttribute('data-resume-after-manual-play')) {
      // Authorize this video for subsequent visibility resumes, including when
      // the first manual attempt followed a weak-network autoplay block.
      this.#policy.respectNetwork = false;
      this.#policy.blocked = false;
    }
    if (!this.#policy.begin(automatic)) return;
    const revision = this.#policy.revision;
    this.refs.video.play().then(() => {
      if (this.closest('[hidden]') || (automatic && !this.hasAttribute('autoplay'))) this.#pause();
    }).catch(() => {
      if (revision === this.#policy.revision && this.hasAttribute('data-homepage-video')) this.#policy.fail();
    });
  }

  #pause() {
    this.refs.video.pause();
    this.#policy?.pause();
  }
}

if (!customElements.get('in-view-video')) {
  customElements.define('in-view-video', InViewVideo);
}
